import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ExpenseService } from '../../services/expense.service';
import { SharedExpenseGroup, SharedHistoryEntry, SharedHistoryDay } from '../../interfaces/expense.interface';

@Component({
    selector: 'app-unified-expenses-table',
    templateUrl: './unified-expenses-table.component.html',
    styleUrls: ['./unified-expenses-table.component.scss']
})
export class UnifiedExpensesTableComponent implements OnInit, OnChanges {
    @Input() sharedGroup!: SharedExpenseGroup;
    @Input() serviceId!: number;
    @Input() serviceStatusId!: number;

    expenseRows: SharedHistoryEntry[] = [];
    isLoading = false;
    showForm = false;
    isSaving = false;

    addForm: FormGroup;

    paymentMethods: { id: number; name: string }[] = [];

    expenseTypes = [
        { value: 'fuel', label: 'Combustible' },
        { value: 'toll', label: 'Caseta en efectivo' },
        { value: 'accommodation', label: 'Hospedaje' },
        { value: 'fine', label: 'Multa de tránsito' },
        { value: 'food', label: 'Alimentos' },
        { value: 'other', label: 'Otros gastos' }
    ];

    constructor(
        private expenseService: ExpenseService,
        private fb: FormBuilder
    ) {
        this.addForm = this.fb.group({
            expense_type: ['', Validators.required],
            amount: ['', [Validators.required, Validators.min(0.01)]],
            payment_method_id: ['', Validators.required],
            description: ['']
        });
    }

    ngOnInit(): void {
        this.loadExpenses();
        this.loadPaymentMethods();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['sharedGroup'] && !changes['sharedGroup'].firstChange) {
            this.loadExpenses();
        }
    }

    get canAddExpenses(): boolean {
        return [1, 2, 3].includes(this.serviceStatusId);
    }

    loadExpenses(): void {
        if (!this.serviceId) return;
        this.isLoading = true;
        this.expenseService.getSharedExpenseHistory(this.serviceId).subscribe({
            next: (res) => {
                const allEntries: SharedHistoryEntry[] = [];
                (res.data || []).forEach((day: SharedHistoryDay) => {
                    day.entries
                        .filter(e => e.event_type === 'expense_created')
                        .forEach(e => allEntries.push(e));
                });
                this.expenseRows = allEntries;
                this.isLoading = false;
            },
            error: () => { this.isLoading = false; }
        });
    }

    loadPaymentMethods(): void {
        this.expenseService.getPaymentMethods().subscribe({
            next: (methods) => { this.paymentMethods = methods; },
            error: () => {}
        });
    }

    openAddForm(): void {
        this.addForm.reset();
        this.showForm = true;
    }

    cancelAdd(): void {
        this.showForm = false;
        this.addForm.reset();
    }

    saveExpense(): void {
        if (this.addForm.invalid || !this.sharedGroup) return;
        this.isSaving = true;
        const payload = {
            expense_type: this.addForm.value.expense_type,
            amount: parseFloat(this.addForm.value.amount),
            payment_method_id: parseInt(this.addForm.value.payment_method_id),
            description: this.addForm.value.description || undefined
        };
        this.expenseService.createUnifiedExpense(this.sharedGroup.group_id, payload).subscribe({
            next: () => {
                this.isSaving = false;
                this.showForm = false;
                this.addForm.reset();
                this.loadExpenses();
            },
            error: () => { this.isSaving = false; }
        });
    }

    expenseTypeLabel(type: string | undefined): string {
        const found = this.expenseTypes.find(t => t.value === type);
        return found ? found.label : (type || '');
    }

    formatAmount(amount: number | undefined): string {
        if (amount == null) return '';
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
            minimumFractionDigits: 2
        }).format(amount);
    }
}
